/*
 * Date Format 1.2.3
 * (c) 2007-2009 Steven Levithan <stevenlevithan.com>
 * MIT license
 *
 * Includes enhancements by Scott Trenda <scott.trenda.net>
 * and Kris Kowal <cixar.com/~kris.kowal/>
 *
 * Accepts a date, a mask, or a date and a mask.
 * Returns a formatted version of the given date.
 * The date defaults to the current date/time.
 * The mask defaults to dateFormat.masks.default.
 */

import moment from "moment";

var dateFormat = (function () {
  var token = /d{1,4}|m{1,4}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g,
    timezone =
      /\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\d{4})?)\b/g,
    timezoneClip = /[^-+\dA-Z]/g,
    pad = function (val, len) {
      val = String(val);
      len = len || 2;
      while (val.length < len) val = "0" + val;
      return val;
    };

  // Regexes and supporting functions are cached through closure
  return function (date, mask, utc) {
    var dF = dateFormat;

    // You can't provide utc if you skip other args (use the "UTC:" mask prefix)
    if (
      arguments.length === 1 &&
      Object.prototype.toString.call(date) === "[object String]" &&
      !/\d/.test(date)
    ) {
      mask = date;
      date = undefined;
    }

    // Passing date through Date applies Date.parse, if necessary
    date = date ? new Date(date) : new Date();
    if (isNaN(date)) throw SyntaxError("invalid date");

    // mask = String(dF.masks[mask] || mask || dF.masks['default']);
    mask = String(masks[mask] || mask || masks["default"]);

    // Allow setting the utc argument via the mask
    if (mask.slice(0, 4) === "UTC:") {
      mask = mask.slice(4);
      utc = true;
    }

    var _ = utc ? "getUTC" : "get",
      d = date[_ + "Date"](),
      D = date[_ + "Day"](),
      m = date[_ + "Month"](),
      y = date[_ + "FullYear"](),
      H = date[_ + "Hours"](),
      M = date[_ + "Minutes"](),
      s = date[_ + "Seconds"](),
      L = date[_ + "Milliseconds"](),
      o = utc ? 0 : date.getTimezoneOffset(),
      flags = {
        d: d,
        dd: pad(d),
        ddd: dF.i18n.dayNames[D],
        dddd: dF.i18n.dayNames[D + 7],
        m: m + 1,
        mm: pad(m + 1),
        mmm: dF.i18n.monthNames[m],
        mmmm: dF.i18n.monthNames[m + 12],
        yy: String(y).slice(2),
        yyyy: y,
        h: H % 12 || 12,
        hh: pad(H % 12 || 12),
        H: H,
        HH: pad(H),
        M: M,
        MM: pad(M),
        s: s,
        ss: pad(s),
        l: pad(L, 3),
        L: pad(L > 99 ? Math.round(L / 10) : L),
        t: H < 12 ? "a" : "p",
        tt: H < 12 ? "am" : "pm",
        T: H < 12 ? "A" : "P",
        TT: H < 12 ? "AM" : "PM",
        Z: utc
          ? "UTC"
          : (String(date).match(timezone) || [""])
              .pop()
              .replace(timezoneClip, ""),
        o:
          (o > 0 ? "-" : "+") +
          pad(Math.floor(Math.abs(o) / 60) * 100 + (Math.abs(o) % 60), 4),
        S: ["th", "st", "nd", "rd"][
          d % 10 > 3 ? 0 : (((d % 100) - (d % 10) !== 10) * d) % 10
        ],
      };

    return mask.replace(token, function ($0) {
      return $0 in flags ? flags[$0] : $0.slice(1, $0.length - 1);
    });
  };
})();

// Some common format strings
export const masks = {
  /**
   * ddd mmm dd yyyy HH:MM:ss
   */
  default: "ddd mmm dd yyyy HH:MM:ss",
  shortDate: "m/d/yy",
  mediumDate: "mmm d, yyyy",
  longDate: "mmmm d, yyyy",
  fullDate: "dddd, mmmm dd, yyyy",
  shortTime: "h:MM TT",
  mediumTime: "h:MM:ss TT",
  longTime: "h:MM:ss TT Z",
  isoDate: "yyyy-mm-dd",
  isoTime: "HH:MM:ss",
  isoDateTime: "yyyy-mm-dd'T'HH:MM:ss",
  isoUtcDateTime: "UTC:yyyy-mm-dd'T'HH:MM:ss'Z'",

  /**
   * 'dddd HH:MM:ss'
   */
  dayTimeStr: "dddd HH:MM:ss",

  /**
   * 'ddd HH:MM:ss'
   */
  dateTimeStr: "ddd HH:MM:ss",

  timeDayDateStr: "HH:MM:ss, ddd mmm dd",

  /**
   * yyyy-mm-dd, ddd, HH:MM:ss
   */
  dateTimeDayStr: "yyyy-mm-dd, ddd, HH:MM:ss",

  /**
   * 'yyyy-mm-dd, ddd'
   */
  dateDayStr: "yyyy-mm-dd, ddd",

  timeStr: "HH:MM",
};

// Internationalization strings
dateFormat.i18n = {
  dayNames: [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  monthNames: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
};

// For convenience...
// eslint-disable-next-line no-extend-native

// usage new Date().format( <mask> ) mask 'dd mm yyy HH:MM:ss'
// Date.prototype.format = function (utc, mask) {
//     return dateFormat(this, mask, utc);
// };

const fromEpoch = (epoch) => {
  var date = new Date(epoch);

  var hours = date.getHours();
  var minutes = date.getMinutes();
  var sec = date.getSeconds();

  // var ampm = hours >= 12 ? 'pm' : 'am';
  // hours = hours % 12;
  // hours = hours ? hours : 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? "0" + minutes : minutes;
  sec = sec < 10 ? "0" + sec : sec;

  var strTime = hours + ":" + minutes + ":" + sec; //+ ampm;
  // return date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear() + '  ' + strTime;

  return (
    date.getDate() +
    "/" +
    (date.getMonth() + 1) +
    "/" +
    date.getFullYear() +
    "  " +
    strTime
  );
};

const formatDate = (date, mask, utc) => {
  return dateFormat(date, mask, utc);
};

const format = (date, mask, utc) => {
  return dateFormat(date, mask, utc);
};

const epochToDate = (epoch) => {
  var d = new Date(0); // The 0 there is the key, which sets the date to the epoch
  d.setUTCSeconds(epoch);

  return d;
};

const epochToDateStr = (epoch, mask, utc) => {
  var d = new Date(0); // The 0 there is the key, which sets the date to the epoch

  d.setUTCSeconds(epoch);

  return format(d, mask, utc);
};

/**
 *
 * formats epoch in ms  to date str
 *
 * @param {number} epoch in ems
 * @param {string} mask format mask, see masks
 * @param {bool} utc  true or false for utc
 * @returns
 */
const emsDate = (epoch, mask, utc) => {
  if (epoch === isNaN() || epoch === undefined || epoch === null) {
    return "-";
  }

  return epochToDateStr(epoch / 1000, mask, utc);
};

const getDurationStr = (start, end) => {
  const diff = end - start;
  let f = moment.utc(diff).format("HH:mm:ss");
  return f;
};

const epoch2LocalTimeStr = (epoch, mask) => {
  const d = epochToDate(epoch);

  return format(d, mask);
};

const stringToDate = (_date, _format, _delimiter) => {
  var formatLowerCase = _format.toLowerCase();
  var formatItems = formatLowerCase.split(_delimiter);
  var dateItems = _date.split(_delimiter);
  var monthIndex = formatItems.indexOf("mm");
  var dayIndex = formatItems.indexOf("dd");
  var yearIndex = formatItems.indexOf("yyyy");
  var month = parseInt(dateItems[monthIndex]);
  month -= 1;
  var formatedDate = new Date(dateItems[yearIndex], month, dateItems[dayIndex]);
  return formatedDate;
};

/**
 *
 * @param {numb} ems unix timestamp in ms
 * @returns unit timestamp start of date 2023 11 5 00:00:00, thus without min, sec, ms
 */
const startOfDate = (ems) => {
  const d = new Date(ems);
  d.setUTCHours(0, 0, 0, 0);

  const epoch = d.getTime();
  return epoch;
};

/**
 *
 * @param {number} ems time in epoch ms
 * @returns the time without seconds and ms
 */
const toMinute = (ems) => {
  const d = new Date(ems);

  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  d.setUTCHours(h, m, 0, 0);

  const r = d.getTime();
  return r;
};

const toHour = (ems) => {
  const d = new Date(ems);

  const h = d.getUTCHours();
  d.setUTCHours(h, 0, 0, 0);

  const r = d.getTime();
  return r;
};

const timeToMinute = () => {
  const d = Date.now();
  return toMinute(d);
};

const toMonth = (old) => {
  const date = new Date(old);
  const target = new Date(date.getUTCFullYear(), date.getUTCMonth(), 1);
  const offset = date.getTimezoneOffset();
  if (offset === -60) {
    let hours = offset / -60 - 1;

    target.setUTCHours(hours, 0, 0, 0);
  } else {
    target.setUTCHours(0, 0, 0, 0);
  }

  const r = addDay(target.getTime(), 1);
  return r;
};

const toEpochMs = (date) => {
  const r = new Date(date).getTime();
  return r;
};

/**
 *
 *
 * @param {*} date date is in user local
 * @returns converted epoch, including daylight
 */
const localToEpoch = (date) => {
  var str = date.toUTCString();

  // returns it in seconds
  var epoch = moment(str).unix() * 1000;
  return epoch;
};

/**
 * calculates the date + days to add
 * retuns that date at 00:00
 *
 * @param {number} old the  date
 * @param {number} days2add days to added
 *
 *
 * @returns new date, at 00:00
 */
const addDay = (old, days2add) => {
  const d = new Date(old);
  const target = d.setDate(d.getDate() + days2add);

  // const temp = new Date(target);
  return target;
};

const addMonths = (date, months2Add) => {
  const d = new Date(date);

  var r = d.setUTCMonth(d.getUTCMonth() + months2Add);
  //   console.log(`addMonth: ${r}`);
  return r;
};

const addHours = (date, hours2Add) => {
  const d = new Date(date);

  var r = d.setUTCHours(d.getUTCHours() + hours2Add);
  //   console.log(`addMonth: ${r}`);
  return r;
};

const addMinutes = (date, minutes2Add) => {
  const d = new Date(date);

  var r = d.setUTCMinutes(d.getUTCMinutes() + minutes2Add);
  //   console.log(`addMonth: ${r}`);
  return r;
};

/**
 *
 * returns today at start in ems
 *
 * @returns start of today in ems
 */
const today = () => {
  return startOfDate(Date.now());
};

/*
 * returns epoch date in string format
 *
 * @param {number} d the date
 * @returns date string ddd dd mmm yyyy
 */
const dateStr = (d, utc) => {
  if (utc === undefined) utc = "";

  return epochToDateStr(d / 1000, "ddd dd mmm yyyy", utc);
};

export function isCurrentDay(ems) {
  const now = new Date();

  // get selected date
  const date = new Date(ems);

  if (date.getFullYear() !== now.getFullYear()) {
    return false;
  }
  if (date.getUTCMonth() !== now.getUTCMonth()) {
    return false;
  }

  if (date.getUTCDate() !== now.getUTCDate()) {
    return false;
  }

  return true;
}

export function isSameDay(source, ems) {
  const now = new Date(source);

  // get selected date
  const date = new Date(ems);

  if (date.getYear() !== now.getYear()) {
    return false;
  }
  if (date.getMonth() !== now.getMonth()) {
    return false;
  }

  if (date.getDate() !== now.getDate()) {
    return false;
  }

  return true;
}

export const MillisecondsInDay = 86400000;

const df = {
  stringToDate,
  formatDate,
  format,
  fromEpoch,
  epochToDate,
  epochToDateStr,

  today,
  dateStr,
  masks,
  MillisecondsInDay,
  emsDate,

  toEpochMs,

  epoch2LocalTimeStr,
  startOfDate,
  addDay,

  toMinute,
  toHour,
  timeToMinute,

  toMonth,
  addMonths,
  addHours,
  addMinutes,

  getDurationStr,
  localToEpoch,
};

export default df;
